/**
 * @file Mobile navigation — drawer toggle and accordion behaviour.
 */

import { animate, stagger } from 'motion';
import { debounce } from '../../../../../assets/js/_helpers.js';

/** Duration in seconds for drawer and accordion animations. */
const DURATION = 0.25;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Stagger interval between each nav item fade-in, in seconds. */
const STAGGER_INTERVAL = reducedMotion ? 0.01 : 0.04;

/** Duration of each individual nav item fade-in, in seconds. */
const FADE_DURATION = reducedMotion ? 0.01 : 0.2;

/**
 * Initialises the mobile navigation.
 * Call once on DOMContentLoaded.
 */
export function navMobile() {
  const nav = document.getElementById('nav-mobile');
  if (!nav) return;

  const toggle = nav.querySelector('.menu-toggle');
  const drawer = document.getElementById('nav-mobile-drawer');
  if (!toggle || !drawer) return;

  initDrawer(toggle, drawer);
  initAccordions(drawer);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !isClosed(drawer)) {
      closeDrawer(toggle, drawer, true);
    }
  });

  // The viewport can change height while the drawer is open (rotation, the
  // mobile browser chrome showing/hiding on scroll) — keep the cap current.
  window.addEventListener('resize', debounce(() => {
    if (!isClosed(drawer)) setDrawerMaxHeight(drawer);
  }, 150));
}

// State helpers

/**
 * Returns true when an element is in its closed (inert) state.
 *
 * @param {HTMLElement} el
 * @returns {boolean}
 */
const isClosed = el => el.hasAttribute('inert');

// Drawer

/**
 * Caps the drawer's height to the space remaining below it in the viewport,
 * so content taller than that scrolls inside the drawer (via its
 * `overflow-y-auto`) instead of extending past the bottom of the screen
 * with no way to reach it.
 *
 * @param {HTMLElement} drawer
 */
function setDrawerMaxHeight(drawer) {
  const available = window.innerHeight - drawer.getBoundingClientRect().top;
  drawer.style.maxHeight = `${available}px`;
}

/**
 * Wires the hamburger toggle button to open and close the drawer.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} drawer
 */
function initDrawer(toggle, drawer) {
  drawer.setAttribute('inert', '');
  toggle.setAttribute('aria-expanded', 'false');

  toggle.addEventListener('click', () => {
    if (isClosed(drawer)) {
      openDrawer(toggle, drawer);
      return;
    }

    closeDrawer(toggle, drawer);

    // Otherwise the still-focused button keeps the header's :focus-within reveal engaged.
    toggle.blur();
  });
}

/**
 * Opens the navigation drawer.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} drawer
 */
function openDrawer(toggle, drawer) {
  setDrawerMaxHeight(drawer);
  drawer.removeAttribute('inert');
  toggle.setAttribute('aria-expanded', 'true');
  toggle.setAttribute('aria-label', 'Close menu');
  animate(drawer, { height: 'auto' }, { duration: DURATION });

  // Collect top-level LIs from both navs in document order so the stagger
  // runs through the main nav items first, then the utility nav items.
  const items = [
    ...drawer.querySelectorAll('nav[aria-label="Mobile navigation"] > ul > li'),
    ...drawer.querySelectorAll('nav[aria-label="Utility navigation"] > ul > li'),
  ];

  if (items.length) {
    // Reset so re-opens always start from opacity 0.
    items.forEach(li => { li.style.opacity = '0'; });
    animate(items, { opacity: [0, 1] }, { delay: stagger(STAGGER_INTERVAL), duration: FADE_DURATION });
  }
}

/**
 * Closes the navigation drawer and optionally returns focus to the toggle.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} drawer
 * @param {boolean} [returnFocus=false]
 */
function closeDrawer(toggle, drawer, returnFocus = false) {
  drawer.setAttribute('inert', '');
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-label', 'Open menu');
  animate(drawer, { height: 0 }, { duration: DURATION });

  // Reset LI opacities so the next open always starts from 0.
  drawer.querySelectorAll('nav > ul > li').forEach(li => { li.style.opacity = '0'; });

  if (returnFocus) toggle.focus();
}

// Accordions

/**
 * Initialises all accordion toggles found within the drawer.
 *
 * @param {HTMLElement} drawer
 */
function initAccordions(drawer) {
  drawer.querySelectorAll('.nav-mobile__accordion-toggle').forEach(toggle => initAccordion(toggle));
}

/**
 * Wires a single accordion toggle.
 *
 * @param {HTMLElement} toggle
 */
function initAccordion(toggle) {
  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  panel.setAttribute('inert', '');
  panel.style.height = '0';
  toggle.setAttribute('aria-expanded', 'false');

  toggle.addEventListener('click', () => {
    isClosed(panel) ? openAccordion(toggle, panel) : closeAccordion(toggle, panel);
  });
}

/**
 * Opens an accordion panel.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} panel
 */
function openAccordion(toggle, panel) {
  panel.removeAttribute('inert');
  toggle.setAttribute('aria-expanded', 'true');
  animate(panel, { height: 'auto' }, { duration: DURATION });
}

/**
 * Closes an accordion panel.
 *
 * @param {HTMLElement} toggle
 * @param {HTMLElement} panel
 */
function closeAccordion(toggle, panel) {
  panel.setAttribute('inert', '');
  toggle.setAttribute('aria-expanded', 'false');
  animate(panel, { height: 0 }, { duration: DURATION });
}

